import { supabase, supabaseProjectRef, isSupabaseConfigured } from './supabase';
import {
  notifyVerifiedProfileChange,
} from './publicSessionCache';
import {
  clearAuthFlowMarker,
  clearAuthRedirectFlags,
  clearPaymentFlags,
  clearPrePaymentMarkers,
  setAuthFlowMarker,
  setSignedOutAt,
} from '../utils/authFlow';

export const buildSupabaseStorageKeys = (projectRef) => {
  const keys = [
    'supabase.auth.token',
    'sb-access-token',
    'sb-refresh-token',
  ];

  if (projectRef) {
    keys.push(`sb-${projectRef}-auth-token`);
  }

  return keys;
};

export const clearSupabaseAuthStorage = (projectRef = supabaseProjectRef) => {
  const keys = buildSupabaseStorageKeys(projectRef);

  keys.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('[authSessionHelpers] Could not clear localStorage auth key:', key, error);
    }

    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn('[authSessionHelpers] Could not clear sessionStorage auth key:', key, error);
    }
  });
};

export const signInWithGoogleSession = async ({ forceAccountSelection = false } = {}) => {
  if (!isSupabaseConfigured) {
    throw new Error('Supabase auth is not configured in the frontend. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in Vercel, then redeploy.');
  }

  setAuthFlowMarker();

  const queryParams = forceAccountSelection ? { prompt: 'select_account' } : undefined;
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
      ...(queryParams ? { queryParams } : {}),
    },
  });

  if (error) {
    clearAuthRedirectFlags();
    throw error;
  }
};

export const signOutSession = async () => {
  let signOutError = null;

  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      signOutError = error;
    }
  } catch (error) {
    signOutError = error;
  } finally {
    clearSupabaseAuthStorage();
    clearAuthFlowMarker();
    clearPrePaymentMarkers();
    clearPaymentFlags();
    setSignedOutAt();
  }

  if (!signOutError) {
    notifyVerifiedProfileChange(null);
    return;
  }

  const message = String(signOutError.message || '').toLowerCase();
  const isNormalSignOutError =
    message.includes('session missing') ||
    message.includes('session not found') ||
    message.includes('no session') ||
    message.includes('invalid session');

  if (!isNormalSignOutError) {
    throw signOutError;
  }

  notifyVerifiedProfileChange(null);
};
